import React, {useCallback, useState} from 'react';
import {Box, Button, Typography, Backdrop, CircularProgress} from '@mui/material';
import RefreshIcon from "@mui/icons-material/Refresh";

import LoadingProgressBar from '../components/common/LoadingProgressBar';
import MyHistorySessionsTable, {PAGE_SIZE_OPTIONS} from '../components/my-history/MyHistorySessionsTable';

import {RefreshProvider} from '../contexts/RefreshContext';
import {SIMPLE_TIME_FORMAT} from "../config/DateTimeConstants";
import {NO_SESSION_HISTORY_MESSAGE} from "../helpers/SessionHelper";

import {useCurrentUser} from "../hooks/useCurrentUser";
import {useTimezone} from "../hooks/useTimezone";
import useSessionHistory from '../hooks/useSessionHistory';

const styles = {
    box: {
        container: {
            backgroundColor: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
            padding: '1rem',
            paddingTop: '0.2rem',
            width: '100%',
            '& > *': {
                backgroundColor: '#FFFFFF',
                width: '100%',
            },
        },
        header: {
            alignItems: 'center',
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '0.5rem',
        }
    },
    typography: {
        lastRefresh: {
            color: '#757575',
            fontSize: '0.75rem',
        }
    },
    refreshButton: {
        minWidth: 'auto',
        width: '2.3rem',
        mt: '0.1rem',
    },
};

export default function MyHistoryView() {
    const {currentUser} = useCurrentUser();
    const {timezone} = useTimezone();
    const performerId = currentUser?.id;

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: PAGE_SIZE_OPTIONS[0],
    });
    const [sortOrder, setSortOrder] = useState([{field: 'sessionDate', sort: 'DESC'}]);
    const [filterModel, setFilterModel] = useState([])

    const {
        sessions,
        serviceTypes,
        totalSessionsCount,
        lastRefreshTime,
        showProgress,
        handleRefresh,
        handleProgressComplete,
    } = useSessionHistory(performerId, timezone, paginationModel, sortOrder, filterModel);

    const handlePaginationModelChange = useCallback((newPaginationModel) => {
        setPaginationModel(newPaginationModel);
    }, []);

    return (
        <RefreshProvider trigger={lastRefreshTime}>
            <Box sx={styles.box.container}>
                <Box sx={styles.box.header}>
                    <Button variant="contained" size="small" sx={styles.refreshButton} onClick={handleRefresh} aria-label="Refresh history">
                        <RefreshIcon/>
                    </Button>
                    <Typography variant="body2" sx={styles.typography.lastRefresh}>
                        (Last refreshed: {lastRefreshTime.tz(timezone).format(SIMPLE_TIME_FORMAT)})
                    </Typography>
                </Box>

                <Box sx={{width: '100%', height: '0.25rem'}}>
                    {
                      showProgress
                      &&
                      <>
                          <LoadingProgressBar onComplete={handleProgressComplete}/>
                          <Backdrop sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}} open={showProgress}>
                              <CircularProgress color="inherit"/>
                          </Backdrop>
                      </>
                    }
                </Box>

                <MyHistorySessionsTable
                    sessions={sessions}
                    serviceTypes={serviceTypes}
                    noRowsMessage={NO_SESSION_HISTORY_MESSAGE}
                    rowCount={totalSessionsCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelChange}
                    setSortOrder={setSortOrder}
                    setFilterModel={setFilterModel}/>
            </Box>
        </RefreshProvider>
    );
}